import { normalizeCategory, normalizeProduct } from '../../normalizers'
import type {
  CatalogCategory,
  CatalogCategoryNode,
  CatalogPagination,
  CatalogProduct,
  CategoryProductsSection,
} from '../../types'

type SafeCategoryRouteData = {
  category: CatalogCategory
  trail: CatalogCategory[]
  children: CatalogCategoryNode[]
  products: CatalogProduct[]
  productsPagination?: CatalogPagination
  categoryProducts: Record<string, CategoryProductsSection>
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const asRecord = (value: unknown): Record<string, unknown> => (isRecord(value) ? value : {})

const toCollection = (value: unknown, nestedKey: 'categories' | 'products'): Record<string, unknown>[] => {
  if (Array.isArray(value)) {
    return value.filter(isRecord)
  }
  const record = asRecord(value)
  const nested = record[nestedKey]
  if (Array.isArray(nested)) {
    return nested.filter(isRecord)
  }
  return []
}

const normalizeCategoryNode = (value: unknown): CatalogCategoryNode | null => {
  if (!isRecord(value)) return null
  const category = normalizeCategory(value)
  if (!category._id || !category.slug) return null
  const children = toCollection(value.children, 'categories')
    .map(normalizeCategoryNode)
    .filter((entry): entry is CatalogCategoryNode => Boolean(entry))
  return { ...category, children }
}

const toPositiveInteger = (value: unknown, fallback: number): number => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback
  return Math.trunc(parsed)
}

const toSafePagination = (value: unknown): CatalogPagination | undefined => {
  const source = asRecord(value)
  const paginationSource = isRecord(source.pagination) ? source.pagination : source
  if (!Object.keys(paginationSource).length) return undefined

  const total = toPositiveInteger(paginationSource.total, 0)
  const limit = toPositiveInteger(paginationSource.limit, 9)
  const page = toPositiveInteger(paginationSource.page, 1)
  const computedTotalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)))
  const totalPages = toPositiveInteger(paginationSource.totalPages, computedTotalPages)
  const safePage = Math.min(page, totalPages)

  return {
    page: safePage,
    limit,
    total,
    totalPages,
    hasNextPage: Boolean(paginationSource.hasNextPage ?? safePage < totalPages),
    hasPreviousPage: Boolean(paginationSource.hasPreviousPage ?? safePage > 1),
  }
}

const normalizeCategoryProducts = (value: unknown): Record<string, CategoryProductsSection> => {
  if (!isRecord(value)) return {}

  return Object.entries(value).reduce<Record<string, CategoryProductsSection>>((result, [categoryId, rawSection]) => {
    if (!isRecord(rawSection)) return result
    const products = toCollection(rawSection.products, 'products')
      .map((entry) => normalizeProduct(entry))
      .filter((entry) => Boolean(entry._id && entry.slug))
    const pagination = toSafePagination(rawSection.pagination)
    if (products.length || pagination) result[categoryId] = { products, pagination }
    return result
  }, {})
}

const FALLBACK_CATEGORY: CatalogCategory = {
  _id: '',
  name: 'Categoria',
  slug: 'categoria',
  description: '',
  parentCategoryId: null,
  status: 'active',
  imageVariants: null,
}

export const normalizeCategoryRouteData = (value: unknown): SafeCategoryRouteData => {
  const record = asRecord(value)
  const trail = toCollection(record.trail, 'categories')
    .map((entry) => normalizeCategory(entry))
    .filter((entry) => Boolean(entry._id && entry.slug))

  const children = toCollection(record.children, 'categories')
    .map(normalizeCategoryNode)
    .filter((entry): entry is CatalogCategoryNode => Boolean(entry))

  const products = toCollection(record.products, 'products')
    .map((entry) => normalizeProduct(entry))
    .filter((entry) => Boolean(entry._id && entry.slug))

  const rawCategory = isRecord(record.category) ? normalizeCategory(record.category) : null
  const categoryFromTrail = trail[trail.length - 1] ?? null
  const categoryFromChildren = children[0] ?? null
  const category =
    rawCategory && rawCategory._id && rawCategory.slug
      ? rawCategory
      : categoryFromTrail ?? categoryFromChildren ?? FALLBACK_CATEGORY

  return {
    category,
    trail,
    children,
    products,
    productsPagination: toSafePagination(record.productsPagination),
    categoryProducts: normalizeCategoryProducts(record.categoryProducts),
  }
}
